import React, { useState, useEffect, useRef, ReactElement } from 'react';
import Notification from '@Common/Notification';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import { useSearchLocationContacts, useCreateLocationContact, useRemoveLocationContact, Contact } from '@Apis/locationContact';
import { useSearchResponsibleUsers } from '@Apis/todaysFocus';
import { LocationDetailData } from '../../types';
import './LocationDetailContactsTile.scss';

interface LocationDetailContactsTileProps {
    location: LocationDetailData;
}

interface CandidateUser {
    id: number;
    name: string;
    jobTitle?: string;
}

const LocationDetailContactsTile = ({ location }: LocationDetailContactsTileProps): ReactElement => {
    const [showLinkPopover, setShowLinkPopover] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

    const popoverRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ---- Contacts infinite query ----
    const {
        data: contactPages,
        isPending: isLoading,
        hasNextPage: showMore,
        fetchNextPage: onShowMore,
    } = useSearchLocationContacts(location.locationKey, 'firstName');

    // Flatten pages and filter to active-only
    const contacts: Contact[] = (contactPages?.pages ?? [])
        .flatMap(page => page.results)
        .filter(c => c.isActive !== false);

    // ---- Mutations ----
    const createContact = useCreateLocationContact(location.locationKey);
    const removeContact = useRemoveLocationContact(location.locationKey);

    // ---- Candidate search (reuse existing hook from todaysFocus) ----
    // Gate by passing empty locationKeys when search term is too short — the hook
    // uses `enabled: locationKeys.length > 0` so it won't fire.
    const { data: responsibleUsersData, isFetching: isCandidatesFetching } = useSearchResponsibleUsers({
        locationKeys: debouncedSearchTerm.trim().length > 1 ? [location.locationKey] : [],
        searchTerm: debouncedSearchTerm,
    });
    const candidates: CandidateUser[] = (responsibleUsersData?.users ?? []).map(u => ({
        id: u.id,
        name: u.name,
    }));

    // ---- Debounce search term ----
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (searchTerm.trim().length <= 1) {
            setDebouncedSearchTerm('');
            return;
        }
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchTerm]);

    // ---- Close popover on outside click or Escape ----
    useEffect(() => {
        if (!showLinkPopover) return;

        const handleMouseDown = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setShowLinkPopover(false);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowLinkPopover(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showLinkPopover]);

    const onRemoveContact = async (contact: Contact) => {
        try {
            await removeContact.mutateAsync({ locationKey: location.locationKey, userId: contact.userId });
            Notification('success', '', 'Contact has been removed');
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onLinkUser = async (user: CandidateUser) => {
        try {
            await createContact.mutateAsync({ locationKey: location.locationKey, userId: user.id });
            Notification('success', '', 'Location Contact Added');
            setSearchTerm('');
            setDebouncedSearchTerm('');
            setShowLinkPopover(false);
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const isSearching = isCandidatesFetching && debouncedSearchTerm.trim().length > 1;

    return (
        <div className='location-detail-contacts-tile'>
            <LoadingIndicator isLoading={isLoading}>
                <div className='contact-cards'>
                    {contacts.map(contact => (
                        <div key={contact.userId} className='contact-card'>
                            <button
                                type='button'
                                className='contact-card-remove'
                                title='Remove contact'
                                aria-label='Remove contact'
                                onClick={() => onRemoveContact(contact)}>
                                <i className='fas fa-trash-can' />
                            </button>

                            <div className='contact-name'>{contact.firstName} {contact.lastName}</div>
                            <div className='contact-title'>{contact.jobTitle}</div>

                            {contact.phoneNumber &&
                                <div className='contact-line'>
                                    <i className='fa-regular fa-phone' />
                                    <span>{contact.phoneNumber}</span>
                                </div>
                            }

                            {contact.emailAddress &&
                                <div className='contact-line'>
                                    <i className='fa-regular fa-envelope' />
                                    <a href={`mailto:${contact.emailAddress}`} className='contact-email'>{contact.emailAddress}</a>
                                </div>
                            }
                        </div>
                    ))}

                    {!isLoading && !contacts.length &&
                        <div className='contacts-empty'>No contacts</div>
                    }
                </div>

                {showMore &&
                    <button type='button' className='contacts-show-more' onClick={() => onShowMore()}>Show More</button>
                }

                <div className='contacts-footer'>
                    <span className='contacts-count'>{contacts.length} contact{contacts.length === 1 ? '' : 's'}</span>
                    <div className='link-contact-wrap' ref={popoverRef}>
                        {showLinkPopover &&
                            <div className='link-contact-popover'>
                                <div className='link-contact-popover-header'>Select a contact to add</div>
                                <input
                                    type='text'
                                    className='link-contact-search'
                                    placeholder='Search'
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                                <div className='link-contact-list'>
                                    {isSearching && <div className='link-contact-status'>Searching…</div>}
                                    {!isSearching && candidates.map(user => (
                                        <button type='button' key={user.id} className='link-contact-item' onClick={() => onLinkUser(user)}>
                                            <div className='link-contact-item-name'>{user.name}</div>
                                            {user.jobTitle && <div className='link-contact-item-title'>{user.jobTitle}</div>}
                                        </button>
                                    ))}
                                    {!isSearching && debouncedSearchTerm.trim().length > 1 && candidates.length === 0 &&
                                        <div className='link-contact-status'>No matches</div>}
                                </div>
                            </div>
                        }
                        <button type='button' className='link-contact-btn' onClick={() => {
                            setShowLinkPopover(o => {
                                if (!o) {
                                    setSearchTerm('');
                                    setDebouncedSearchTerm('');
                                }
                                return !o;
                            });
                        }}>
                            <i className='fa-regular fa-plus' />
                            <span>Link Contact</span>
                        </button>
                    </div>
                </div>
            </LoadingIndicator>
        </div>
    );
};

export default LocationDetailContactsTile;
